/**
 * Watch a prescribed exercise, at exactly the settings being prescribed.
 *
 * Every clinical correction in Phase 2 came from watching an exercise run, and
 * none came from reading its parameters. Target size, speed, contrast and path
 * geometry are judgements the eye makes, so setting a dose without being able
 * to see it is guesswork with a number attached.
 *
 * This runs the real exercise through the real runner with the real parameter
 * values from the builder — not an illustration of them. It records nothing and
 * prescribes nothing; it is a window, and the exercise cannot tell the
 * difference between this and a patient session.
 *
 * It sits BESIDE the settings rather than over them. It began as a modal, and
 * a modal was the wrong shape: judging a dose means changing a number and
 * seeing what it did, and a window covering the numbers made that a round trip
 * every time. Docked, the exercise restarts as each setting changes, so the
 * picture keeps up with the form.
 *
 * Full screen stays available and still matters — a docked pane is a faithful
 * SCALE MODEL, so it answers shape and proportion, but only full screen shows
 * true visual angle. "Is fifteen degrees right for this patient" is a
 * full-screen question.
 *
 * Contains no exercise-specific code (§1).
 */

import { useEffect, useRef, useState } from 'react';
import type {
  ExerciseDefinition,
  InputMode,
  ParamValues,
  Position,
  StopReason,
} from '@pathways/exercises';
import { ExerciseStage, type ExerciseStageHandle } from './ExerciseStage.js';
import { loadPxPerDegree, savePxPerDegree } from './calibration.js';
import { Stepper } from './ParamForm.js';
import styles from './PreviewPanel.module.css';

export interface PreviewPanelProps {
  definition: ExerciseDefinition;
  params: ParamValues;
  position: Position;
  inputMode: InputMode;
  onClose: () => void;
}

export function PreviewPanel({
  definition,
  params,
  position,
  inputMode,
  onClose,
}: PreviewPanelProps) {
  const stage = useRef<ExerciseStageHandle | null>(null);
  const [running, setRunning] = useState(false);
  // Windowed by default — docked, the point is to watch it without leaving the
  // form. Full screen is one click away when true size is the question.
  const [fullScreen, setFullScreen] = useState(false);
  const [lastStop, setLastStop] = useState<StopReason | null>(null);
  const [pxPerDegree, setPxPerDegree] = useState(loadPxPerDegree);

  useEffect(() => {
    savePxPerDegree(pxPerDegree);
  }, [pxPerDegree]);

  // No Escape-to-close any more. As a modal it was the way out; docked, the
  // panel is part of the page and Escape belongs to leaving full screen alone.

  return (
    <aside className={styles.panel} aria-labelledby="preview-heading">
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.heading} id="preview-heading">
              Preview
            </h2>
            <p className={styles.subheading}>
              {position}, responding with the {inputMode}. Updates as you change
              the settings. Nothing is recorded.
            </p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={() => {
              stage.current?.stop('clinician_stop');
              onClose();
            }}
          >
            Close
          </button>
        </header>

        <ExerciseStage
          ref={stage}
          definition={definition}
          params={params}
          position={position}
          inputMode={inputMode}
          pxPerDegree={pxPerDegree}
          fullScreen={fullScreen}
          onRunningChange={setRunning}
          onStopped={setLastStop}
          idleHint="Press Run to watch it"
        />

        <div className={styles.controls}>
          {running ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => stage.current?.stop('clinician_stop')}
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              className={styles.primary}
              onClick={() => void stage.current?.start()}
            >
              Run
            </button>
          )}

          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={fullScreen}
              disabled={running}
              onChange={(e) => setFullScreen(e.target.checked)}
            />
            Full screen
          </label>

          <div className={styles.calibration}>
            <span className={styles.calibrationLabel}>Your screen</span>
            <Stepper
              id="preview-px-per-degree"
              min={10}
              max={120}
              step={1}
              value={pxPerDegree}
              disabled={running}
              onChange={setPxPerDegree}
            />
            <span className={styles.calibrationUnit}>px per degree</span>
          </div>

          <p className={styles.stopHint}>
            Spacebar stops instantly, the same key the patient uses.
            {lastStop === 'completed' && ' Last run finished on its own.'}
            {lastStop === 'patient_stop' && ' Last run was stopped with the spacebar.'}
          </p>
        </div>

        <section className={styles.instruction}>
          <h3 className={styles.instructionHeading}>What the patient is told</h3>
          <p className={styles.instructionBody}>{definition.patientInstruction}</p>
        </section>
      </div>
    </aside>
  );
}
